import { RefreshCw, Check, AlertCircle, Cloud, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import type { SyncStatusBarProps as SyncStatusBarPropsType } from './types';

export function SyncStatusBar({
  syncState,
  syncProgress,
  syncMessage,
  totalItems,
  processedItems,
  currentType,
  lastSyncTime,
  onSync,
  onCancel,
  getRelativeTime,
  disabled = false,
}: SyncStatusBarPropsType) {
  const isSyncing = syncState === 'syncing';
  const isSuccess = syncState === 'success';
  const isError = syncState === 'error';
  const isIdle = syncState === 'idle';

  const getProgressText = () => {
    if (totalItems > 0 && processedItems > 0) {
      return `${processedItems}/${totalItems}`;
    }
    return null;
  };

  return (
    <div className="border-t border-border bg-muted/30">
      {isSyncing && (
        <div className="h-0.5 bg-muted overflow-hidden">
          <div
            className="h-full bg-primary transition-all duration-200 ease-out"
            style={{ width: `${Math.min(syncProgress, 100)}%` }}
          />
        </div>
      )}

      <div className="flex items-center">
      <button
        type="button"
        onClick={onSync}
        disabled={isSyncing || disabled}
        className={cn(
          'flex-1 min-w-0 flex items-center justify-between gap-2 px-3 py-2',
          'text-xs',
          'hover:bg-muted/80 transition-colors',
          'disabled:cursor-default',
          isSyncing && 'bg-primary/5',
          isIdle && !disabled && 'hover:bg-muted'
        )}
      >
        <div className="flex items-center gap-2 min-w-0">
          <div
            className={cn(
              'h-2 w-2 rounded-full flex-shrink-0',
              isSyncing && 'bg-primary animate-pulse',
              isSuccess && 'bg-green-500',
              isError && 'bg-destructive',
              isIdle && 'bg-muted-foreground/40'
            )}
          />

          {isSyncing ? (
            <>
              <RefreshCw className="h-3.5 w-3.5 animate-spin text-primary flex-shrink-0" />
              <span className="text-foreground font-medium truncate" title={syncMessage}>
                {currentType || 'Syncing...'}
              </span>
            </>
          ) : isSuccess ? (
            <>
              <Check className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
              <span className="text-green-600 dark:text-green-400 font-medium">Synced</span>
            </>
          ) : isError ? (
            <>
              <AlertCircle className="h-3.5 w-3.5 text-destructive flex-shrink-0" />
              <span className="text-destructive font-medium truncate" title={syncMessage}>
                {syncMessage || 'Sync failed'}
              </span>
            </>
          ) : (
            <>
              <Cloud className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
              <span className="text-muted-foreground">{getRelativeTime(lastSyncTime)}</span>
            </>
          )}
        </div>

        {isSyncing ? (
          <span className="text-[10px] text-primary font-medium tabular-nums flex-shrink-0 bg-primary/10 px-1.5 py-0.5 rounded">
            {getProgressText() || `${Math.round(Math.min(syncProgress, 100))}%`}
          </span>
        ) : (
          isIdle &&
          !disabled && (
            <span className="text-[10px] text-muted-foreground/60 flex-shrink-0">
              Click to sync
            </span>
          )
        )}
      </button>

      {isSyncing && onCancel && (
        <button
          type="button"
          onClick={onCancel}
          className="flex-shrink-0 flex items-center gap-1 h-full px-2.5 py-2 border-l border-border text-destructive/60 hover:text-destructive hover:bg-destructive/10 transition-colors"
          title="Cancel sync"
        >
          <X className="h-3.5 w-3.5" />
          <span className="text-[10px] font-medium">Cancel</span>
        </button>
      )}
      </div>
    </div>
  );
}
